import React, { CSSProperties, Component } from 'react'
import { connect } from "react-redux"
import * as d3 from "d3"
import RenderText from "../render-text"

import { URL } from "app/config/api-config";

import PathShape, { AllPathTypesAndMeanings, PathPoint, PathType } from "../types/PathShape";
import { CoordsType } from "./shape";
import { EditModeType, HelperType } from "app/types/app-types";
import { AnyPoint, Size } from "app/types/model-types";

import { Theme } from "@bdfx/mergado-ui-kit";

import { getHelperContentByActiveShape, setHelperText } from "template-edit/components/helper";
import { recalcShapeSizeAndPointsPositions, sortVerticies, getSVGPathFromPoints, getHandleSize, getFormName } from 'shape-edit/forms/helpers';

import {
    selectFormValues,
    updateInputValue,
} from "app/reducers/form";

import { setHelper } from "actions";

import { DELETE_KEY } from "app/helpers/keymap";
import { CURVED_TEXT_TYPE, TEXT_INSIDE_SHAPE_TYPE } from 'shape-entities/types/shape';
import CurvedTextShape from 'shape-entities/types/CurvedTextShape';
import TextInsideShape, { LineType } from '../types/TextInsideShape';
import { SizeType } from 'products/components/filters';
import { debounce,  omitBy } from 'lodash';
import StrokeUnderlayerFilter, { getStrokeUnderlayerFilterId, strokeUnderlayerFilterUrl } from 'svg-text-utils/StrokeUnderlayerFilter';
import { DO_NOT_SAVE_HISTORY } from 'template-edit/sagas/undoRedoHistory';
import { TemplateOrAlternativeTemplateType } from 'template-edit/undoRedoReducers';
import { GradientDefs, GradientFillUrl } from "shape-edit/forms/fields";

interface Props {
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    isDraggable: boolean
    activeShapeId: string
    data: PathShape | CurvedTextShape | TextInsideShape
    size: SizeType
    helper: HelperType
    editMode: EditModeType
    updateInputValue: typeof updateInputValue
    setHelper: typeof setHelper
}

interface State {
    isActive: boolean
    isNew: boolean
    activeKey: number
    dx: number
    dy: number
    dcx: number
    dcy: number
    dcx2: number
    dcy2: number
    t: PathType
    activeHandleType: string
    left: number
    top: number
    scaleX: number
    scaleY: number
    startX: number
    startY: number
}

export const DEFAULT_PATH_TYPE = 'S'
const { abs, sin, cos, PI, round, acos, sqrt, pow, min, max } = Math

const InitialState: State = {
    isActive: false,
    isNew: false,
    activeKey: null,
    dx: 0,
    dy: 0,
    dcx: 0,
    dcy: 0,
    dcx2: 0,
    dcy2: 0,
    t: DEFAULT_PATH_TYPE,
    activeHandleType: null,
    left: 0,
    top: 0,
    scaleX: 1,
    scaleY: 1,
    startX: 0,
    startY: 0
}

class PathSvg extends Component<Props, State> {
    handleRefs: React.RefObject<SVGRectElement | SVGCircleElement>[] = []
    underlayerFilterId = getStrokeUnderlayerFilterId()
    rectangleRef: React.RefObject<SVGRectElement>
    pathRef: React.RefObject<SVGPathElement>

    constructor(props) {
        super(props)
        this.state = {
            ...InitialState,
            isActive: props.activeShapeId === props.data.index,
        }
        this.rectangleRef = React.createRef()
        this.pathRef = React.createRef()
    }

    getHandleRef = (type: string, index: number): React.RefObject<any> => {
        const refString = `bhandle_${type}${index}`
        if (typeof this.handleRefs[refString] === 'undefined') {
            this.handleRefs[refString] = React.createRef()
        }
        return this.handleRefs[refString]
    }

    bindEvents = (key: number) => {
        ['m', 'c', 'c2'].forEach(r => {
            const ref = this.getHandleRef(r, key).current
            if (ref) {
                d3.select(ref)
                    .call(
                        d3.drag()
                            .on("start", (event: DragEvent) => this.dragStarted(event, key, r))
                            .on("drag", (event: DragEvent) => this.dragged(event))
                            .on("end", (event: DragEvent) => this.dragEnded(event))
                    );
            }
        })
    }

    bindAllEvents = () => {
        const { data } = this.props
        if (data.points) {
            data.points.forEach((point, key) => {
                this.bindEvents(key)
            })
        }
        const el = this.rectangleRef.current
        if (el) {
            d3.select(el)
                .call(
                    d3.drag()
                        .on("start", (event: DragEvent) => this.dragStarted(event, 0, data.type === TEXT_INSIDE_SHAPE_TYPE ? 'c2' : 'c'))
                        .on("drag", (event: DragEvent) => this.dragged(event))
                        .on("end", (event: DragEvent) => this.dragEnded(event))
                );
        }
    }

    bindAllEventsDebounced = debounce(() => {
        this.bindAllEvents()
    }, 400)

    componentDidMount() {
        const { data, form, isAlt } = this.props
        const formName = getFormName(isAlt)

        if (this.props.isDraggable) {
            document.addEventListener("keydown", this.handleKeyDown)
            if (this.state.isActive) {
                this.bindAllEvents()
            }

            const lines = this.getLines(data.points)
            this.props.updateInputValue(formName, `shapes.${data.index}.lines`, lines, DO_NOT_SAVE_HISTORY)
        }
    }

    componentWillUnmount() {
        if (this.props.isDraggable) {
            document.removeEventListener("keydown", this.handleKeyDown)
        }
    }

    setIsActive = () => {
        const { activeKey } = this.state
        const { activeShapeId, data, isDraggable } = this.props

        if (isDraggable) {
            const isActive = activeShapeId === data.index
            if (isActive) {
                this.bindAllEventsDebounced()
            }
            this.setState({
                isActive: isActive,
                activeKey: isActive ? activeKey : null
            })
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        const pointDiff = prevProps && JSON.stringify(this.props.data.points) !== JSON.stringify(prevProps.data.points)

        const { form, isAlt } = this.props

        const formName = getFormName(isAlt)

        if (this.props.isDraggable && prevProps && (
            this.props.activeShapeId !== prevProps.activeShapeId ||
            this.props.data.angle !== prevProps.data.angle ||
            this.props.data.x !== prevProps.data.x ||
            this.props.data.y !== prevProps.data.y
        )
        ) {
            setHelperText(getHelperContentByActiveShape(this.props.data))
            this.setIsActive()    //this may cause loops!
        }

        if (this.props.data.type === TEXT_INSIDE_SHAPE_TYPE && prevProps &&
            (
                this.props.data.text_fontsize !== prevProps.data.text_fontsize ||
                this.props.data.line_spacing !== prevProps.data.line_spacing ||
                this.props.data.width !== prevProps.data.width ||
                this.props.data.height !== prevProps.data.height ||
                pointDiff
            )
        ) {
            const sortedLines = this.getLines(this.props.data.points)
            this.props.updateInputValue(formName, `shapes.${this.props.data.index}.lines`, sortedLines)
        }
    }

    handleKeyDown = (event) => {
        const { activeKey } = this.state
        const { data, activeShapeId, form, isAlt } = this.props

        //do not prevent default for
        if (event.target.type || !this.state.isActive || event.ctrlKey) {
            return;
        }

        const formName = getFormName(isAlt)

        switch (event.keyCode) {
            case DELETE_KEY:
                event.preventDefault()
                if (activeKey !== null) {
                    //delete one point:
                    let updatedPoints = [...data.points]
                    updatedPoints.splice(activeKey, 1)
                    const sortedLines = data.type === TEXT_INSIDE_SHAPE_TYPE ? this.getLines(updatedPoints) : null
                    this.props.updateInputValue(formName, `shapes.${data.index}`, {
                        ...data,
                        points: updatedPoints,
                        lines: sortedLines,
                    })
                } else if (activeShapeId) {
                    //delete layer:
                    const newShapes = omitBy(form.shapes, (value, key) => key === activeShapeId)
                    this.props.updateInputValue(formName, `shapes`, newShapes)
                }
                break;
            default:
                if (['m', 'c', 's', 'a', 'l', 'z', 'h', 't'].includes(event.key)) {
                    const newType = event.key.toUpperCase()
                    this.setState({ t: newType })
                    //rectangle element.style.cursor = this.getCursorByPathType(newType)
                }
        }
    }

    rotateByAlpha = (coords: CoordsType): PathPoint => {
        const { data } = this.props

        const alpha = -((data.angle || 0)) / 180 * PI

        const deltaX = round((coords.x * cos(alpha) - coords.y * sin(alpha)))
        const deltaY = round((coords.x * sin(alpha) + coords.y * cos(alpha)))

        return {
            x: deltaX,
            y: deltaY,
            cx: deltaX,
            cy: deltaY,
            cx2: deltaX,
            cy2: deltaY,
        }
    }

    addPoint = (newPoint: PathPoint) => {
        const { data, isAlt, form } = this.props

        const formName = getFormName(isAlt)

        const isTextInsideShape = data.type === TEXT_INSIDE_SHAPE_TYPE

        const updatedPoints: PathPoint[] = data.points ?
            (isTextInsideShape ? sortVerticies([...data.points, newPoint]) : [...data.points, newPoint]) : [newPoint]

        //find the new point to get active point index:
        let newPointIndex = updatedPoints.length - 1
        updatedPoints.forEach((point, idx: number) => {
            if (point.x === newPoint.x && point.y === newPoint.y) {
                newPointIndex = idx
                return
            }
        })

        this.setState({ activeKey: newPointIndex })

        const updatedShape = recalcShapeSizeAndPointsPositions(form, { ...data, points: updatedPoints })
        const sortedLines = this.getLines(updatedShape.points)

        this.props.updateInputValue(formName, `shapes.${data.index}`, {
            ...updatedShape,
            lines: sortedLines,
        }, DO_NOT_SAVE_HISTORY)

        this.setState({
            dx: 0,
            dy: 0,
            dcx: 0,
            dcy: 0,
            dcx2: 0,
            dcy2: 0,
            isNew: false
        })
    }

    dragStarted = (event, key: number, r: string) => {
        const { data, size } = this.props

        this.setState({
            startX: event.sourceEvent.clientX,
            startY: event.sourceEvent.clientY
        })

        const el = this.rectangleRef.current
        if (el) {
            const isNew = event.sourceEvent.target.tagName === 'rect' && el === event.sourceEvent.target

            const boundingClientRect = el.getBoundingClientRect()
            const scaleX = boundingClientRect.width ? size.width / boundingClientRect.width : 1
            const scaleY = boundingClientRect.height ? size.height / boundingClientRect.height : 1

            const common = {
                activeHandleType: r,
                isNew: isNew,
                left: boundingClientRect.left,
                top: boundingClientRect.top,
                scaleX: scaleX,
                scaleY: scaleY,
                dcx: 0,
                dcy: 0,
                dcx2: 0,
                dcy2: 0,
            }

            if (isNew) {
                //add new point:
                const XPos = round((event.sourceEvent.clientX - boundingClientRect.left) * scaleX - data.x)
                const YPos = round((event.sourceEvent.clientY - boundingClientRect.top) * scaleY - data.y)

                //if shape is rotated, transform coordinates into rotated coordinates:
                const newPoint = this.rotateByAlpha({ x: XPos, y: YPos })

                this.addPoint({
                    ...newPoint,
                    t: this.state.t,
                })

                this.setState({
                    ...common,
                    dx: 0,
                    dy: 0,
                })
            } else {
                this.setState({
                    ...common,
                    activeKey: key,
                })
            }
        }
    }

    dragged = (event) => {
        const { activeKey, activeHandleType, scaleX, scaleY, left, top, startX, startY, isNew } = this.state
        const { data } = this.props

        //to avoid false event.dx and event.dy:
        if ((event.dx && abs(event.dx) > 100) || (event.dy && abs(event.dy) > 100)) {
            return;
        }

        const XPos = round((event.sourceEvent.clientX - left) * scaleX - data.x)
        const YPos = round((event.sourceEvent.clientY - top) * scaleY - data.y)

        //if shape is rotated, transform coordinates into rotated coordinates:
        const newPoint = this.rotateByAlpha({ x: XPos, y: YPos })
        const currentPoint: PathPoint = { ...data.points[activeKey] }

        if (currentPoint.t === 'A') {    //keep rx, ry
            const newPointDiff = this.rotateByAlpha({
                x: round((event.sourceEvent.clientX - startX) * scaleX),
                y: round((event.sourceEvent.clientY - startY) * scaleY)
            })

            //start debug
            const prevPoint = activeKey > 0 ? data.points[activeKey - 1] : null

            const cx = currentPoint.cx + this.state.dcx
            const cy = currentPoint.cy + this.state.dcy

            const xpos = currentPoint.x + this.state.dx
            const ypos = currentPoint.y + this.state.dy

            let rx = abs(cx)
            let ry = abs(cy)

            let param = prevPoint ? this.getCenterParameters(
                prevPoint.x, prevPoint.y,
                xpos, ypos,
                prevPoint.isLargeArc ? 1 : 0,
                prevPoint.isSweep ? 1 : 0,
                rx, ry,
                prevPoint.xRotDegrees ? prevPoint.xRotDegrees : 0
            ) : null

            rx = abs(round(param?.rx ? param.rx : rx))
            ry = abs(round(param?.ry ? param.ry : ry))

            //if (rx !== abs(cx) || ry !== abs(cy)) {
            //https://www.w3.org/TR/SVG2/implnote.html#ArcCorrectionOutOfRangeRadii
            //console.debug(`ellipsis fitted with a different radius: rx=${rx} vs ${cx}, ry=${ry} vs ${cy}`)
            //}
            //end debug

            //arc:
            const dcx = newPointDiff.x
            const dcy = newPointDiff.y

            switch (activeHandleType) {
                case 'c':
                    this.setState({
                        dcx: dcx,
                        dcy: 0,
                    })
                    break;
                case 'c2':
                    this.setState({
                        dcx: 0,
                        dcy: dcy,
                    })
                    break;
                case 'm':
                default:
                    this.setState({
                        dx: (newPoint.x - currentPoint.x),
                        dy: (newPoint.y - currentPoint.y),
                    })
                    break;
            }
        } else {    //other than arc types:
            switch (activeHandleType) {
                case 'c':
                    if (isNew) {
                        this.setState({
                            dcx: -(newPoint.x - currentPoint.cx),
                            dcy: -(newPoint.y - currentPoint.cy),
                            dcx2: (newPoint.x - 2 * currentPoint.x + currentPoint.cx),
                            dcy2: (newPoint.y - 2 * currentPoint.y + currentPoint.cy),
                        })
                    } else {
                        this.setState({
                            dcx: (newPoint.x - currentPoint.cx),
                            dcy: (newPoint.y - currentPoint.cy)
                        })
                    }
                    break;
                case 'c2':
                    if (isNew) {
                        this.setState({
                            dcx: (newPoint.x - currentPoint.cx),
                            dcy: (newPoint.y - currentPoint.cy),
                            dcx2: -(newPoint.x - 2 * currentPoint.x + currentPoint.cx),
                            dcy2: -(newPoint.y - 2 * currentPoint.y + currentPoint.cy),
                        })
                    } else if (currentPoint.t === 'C') {
                        this.setState({
                            dcx2: (newPoint.x - currentPoint.cx2),
                            dcy2: (newPoint.y - currentPoint.cy2),
                        })
                    } else {
                        this.setState({
                            dcx: -(newPoint.x - 2 * currentPoint.x + currentPoint.cx),
                            dcy: -(newPoint.y - 2 * currentPoint.y + currentPoint.cy),
                        })
                    }
                    break;
                case 'm':
                default:
                    const deltaX = (newPoint.x - currentPoint.x)
                    const deltaY = (newPoint.y - currentPoint.y)

                    const isC = currentPoint.t === 'C'

                    this.setState({
                        dx: deltaX,
                        dy: deltaY,
                        dcx: deltaX,
                        dcy: deltaY,
                        dcx2: isC ? deltaX : 0,
                        dcy2: isC ? deltaY : 0,
                    })
                    break;
            }
        }
    }

    dragEnded = (event) => {
        const { data, isAlt, form } = this.props
        const { dx, dy, dcx, dcy, dcx2, dcy2, activeKey, activeHandleType, isNew } = this.state

        const formName = getFormName(isAlt)

        const point = data.points[activeKey]

        let updatedPoint = {
            ...point,
            x: round(point.x + (isNew ? 0 : dx)),
            y: round(point.y + (isNew ? 0 : dy)),
        }

        const cx = round(point.cx + dcx)
        const cy = round(point.cy + dcy)

        const cx2 = round(point.cx2 + dcx2)     //point.cx2    nebo 2 * point.x - cx
        const cy2 = round(point.cy2 + dcy2)

        switch (point.t) {
            case 'A':   //arc
                updatedPoint['cx'] = abs(cx)
                updatedPoint['cy'] = abs(cy)
                break
            case 'C':
                if (isNew) {
                    updatedPoint['cx'] = cx
                    updatedPoint['cy'] = cy

                    if (typeof updatedPoint.cx2 !== 'undefined') {
                        updatedPoint['cx2'] = cx2
                    }
                    if (typeof updatedPoint.cy2 !== 'undefined') {
                        updatedPoint['cy2'] = cy2
                    }
                } else if (activeHandleType === 'c') {
                    updatedPoint['cx'] = cx
                    updatedPoint['cy'] = cy
                } else if (activeHandleType === 'c2') {
                    updatedPoint['cx2'] = cx2
                    updatedPoint['cy2'] = cy2
                }
                break
            case 'S':
                updatedPoint['cx'] = cx
                updatedPoint['cy'] = cy
                updatedPoint['cx2'] = undefined
                updatedPoint['cy2'] = undefined
                break
            default:
                updatedPoint['cx'] = cx
                updatedPoint['cy'] = cy
                if (typeof updatedPoint.cx2 !== 'undefined') {
                    updatedPoint['cx2'] = cx2
                }
                if (typeof updatedPoint.cy2 !== 'undefined') {
                    updatedPoint['cy2'] = cy2
                }
                break
        }

        let updatedPoints = [...data.points]
        updatedPoints[activeKey] = updatedPoint

        const updatedShape = recalcShapeSizeAndPointsPositions(form, { ...data, points: updatedPoints }, `curve${data.index}`)

        const sortedLines = this.getLines(updatedShape.points)
        this.props.updateInputValue(formName, `shapes.${data.index}`, {
            ...updatedShape,
            lines: sortedLines,
        }, DO_NOT_SAVE_HISTORY)

        this.setState({
            dx: 0,
            dy: 0,
            dcx: 0,
            dcy: 0,
            dcx2: 0,
            dcy2: 0,
            isNew: false
        })
    }

    getLines = (points: AnyPoint[]): LineType[] => {
        const { data } = this.props

        if (data.type !== TEXT_INSIDE_SHAPE_TYPE) {
            return null
        }

        const fontSize = data.text_fontsize ? data.text_fontsize : 20
        const lineSpacing = data.line_spacing ? data.line_spacing : 0

        let sortedLines = []
        const gridStepY = fontSize + lineSpacing
        const gridStepX = round(.25 * fontSize)

        const svg: any = document.getElementById('image')

        if (!svg || !points || points.length < 3) {    //at least 3 points to get area
            return sortedLines
        }

        //measure a temporary hidden path built from the given points, so the result matches them
        //even before React re-renders the visible path:
        const path: SVGPathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        path.setAttribute('d', getSVGPathFromPoints(points as PathPoint[]))
        path.setAttribute('visibility', 'hidden')
        path.setAttribute('pointer-events', 'none')
        svg.appendChild(path)

        try {
            //invalid "d" gives an empty geometry - getPointAtLength would throw
            const totalLength = path.getTotalLength()
            if (!(totalLength > 0)) {
                return sortedLines
            }

            let pointsInFill = []

            let minX = null
            let maxX = null
            let minY = null
            let maxY = null

            for (let i = 0; i <= 1; i += .01) {
                const p = path.getPointAtLength(i * totalLength)

                const rounded: AnyPoint = {
                    x: round(p.x / gridStepX) * gridStepX,
                    y: round(p.y / gridStepY) * gridStepY
                }

                minX = minX === null ? rounded.x : min(minX, rounded.x)
                maxX = maxX === null ? rounded.x : max(maxX, rounded.x)
                minY = minY === null ? rounded.y : min(minY, rounded.y)
                maxY = maxY === null ? rounded.y : max(maxY, rounded.y)

                pointsInFill[rounded.y] = pointsInFill[rounded.y] ?? []
                pointsInFill[rounded.y].push(rounded.x)
            }

            let unclosedLineFrom
            let unclosedLineTo
            let point = svg.createSVGPoint()

            for (let y = minY; y <= maxY; y += gridStepY) {
                unclosedLineFrom = null
                unclosedLineTo = null

                for (let x = minX; x <= maxX; x += gridStepX) {
                    Object.assign(point, { x, y })
                    if (path.isPointInFill(point)) {
                        unclosedLineFrom = (unclosedLineFrom !== null) ? unclosedLineFrom : x
                        unclosedLineTo = x
                    } else {    //linebreak
                        if (unclosedLineFrom !== null && unclosedLineTo !== null && Math.abs(unclosedLineTo - unclosedLineFrom) >= gridStepX) {
                            sortedLines.push({
                                x1: unclosedLineFrom,
                                x2: unclosedLineTo,
                                y: y,
                            })
                            //clear
                            unclosedLineFrom = null
                            unclosedLineTo = null
                        }
                    }
                } //end of row

                if (unclosedLineFrom !== null && unclosedLineTo !== null && Math.abs(unclosedLineTo - unclosedLineFrom) >= gridStepX) {
                    sortedLines.push({
                        x1: unclosedLineFrom,
                        x2: unclosedLineTo,
                        y: y,
                    })
                }
            }
        } finally {
            path.remove()
        }

        return sortedLines
    }

    getPointsData = (): string => {
        const { data } = this.props
        const { activeKey, dx, dy, dcx, dcy, dcx2, dcy2 } = this.state

        const pointsList = data.points

        return pointsList ? getSVGPathFromPoints(pointsList, activeKey, dx, dy, dcx, dcy, dcx2, dcy2) : ''
    }

    getEndpointParameters = (cx: number, cy: number, rx: number, ry: number, xAxisRotationDegrees: number, theta: number, dTheta: number): { x1: number, y1: number, x2: number, y2: number, largeArcFlag: number, sweepFlag: number } => {
        const [x1, y1] = this.getEllipsePointForAngle(cx, cy, rx, ry, xAxisRotationDegrees, theta)
        const [x2, y2] = this.getEllipsePointForAngle(cx, cy, rx, ry, xAxisRotationDegrees, theta + dTheta)

        const largeArcFlag = abs(dTheta) > PI ? 1 : 0
        const sweepFlag = dTheta > 0 ? 1 : 0

        return { x1, y1, x2, y2, largeArcFlag, sweepFlag }
    }

    getEllipsePointForAngle = (cx: number, cy: number, rx: number, ry: number, xAxisRotationDegrees: number, theta: number): [number, number] => {
        const M = abs(rx) * cos(theta)
        const N = abs(ry) * sin(theta)

        const a = xAxisRotationDegrees * PI / 180

        return [
            cx + cos(a) * M - sin(a) * N,
            cy + sin(a) * M + cos(a) * N
        ]
    }

    vectorAngle = ([ux, uy], [vx, vy]): number => {
        const sign = ux * vy - uy * vx < 0 ? -1 : 1
        const ua = sqrt(ux * ux + uy * uy)
        const va = sqrt(vx * vx + vy * vy)
        const dot = ux * vx + uy * vy

        return sign * acos(dot / (ua * va))
    }

    getCenterParameters = (x1: number, y1: number, x2: number, y2: number, largeArcFlag: number, sweepFlag: number, rx: number, ry: number, xAxisRotationDegrees: number) => {
        const pw2 = (n) => pow(n, 2)

        const s = sin(xAxisRotationDegrees * PI / 180)
        const c = cos(xAxisRotationDegrees * PI / 180)

        // Step 1: simplify through translation/rotation
        const x = c * (x1 - x2) / 2 + s * (y1 - y2) / 2
        const y = -s * (x1 - x2) / 2 + c * (y1 - y2) / 2

        const px = pw2(x)
        const py = pw2(y)

        let prx = pw2(rx)
        let pry = pw2(ry)

        // correct of out-of-range radii
        const L = px / prx + py / pry

        if (L > 1) {
            rx = sqrt(L) * abs(rx);
            ry = sqrt(L) * abs(ry);

            prx = pw2(rx)
            pry = pw2(ry)
        } else {
            rx = abs(rx);
            ry = abs(ry);
        }

        // Step 2 + 3: compute center
        const sign = largeArcFlag === sweepFlag ? -1 : 1

        const a = prx * pry - prx * py - pry * px
        const M = sqrt(abs(a) / (prx * py + pry * px)) * sign    //avoid negative number in sqrt!!!!!

        const _cx = M * (rx * y) / ry
        const _cy = M * (-ry * x) / rx

        const cx = c * _cx - s * _cy + (x1 + x2) / 2
        const cy = s * _cx + c * _cy + (y1 + y2) / 2

        // Step 4: compute θ and dθ
        const theta = this.vectorAngle([1, 0], [(x - _cx) / rx, (y - _cy) / ry])

        let _dTheta = this.deg(this.vectorAngle([(x - _cx) / rx, (y - _cy) / ry], [(-x - _cx) / rx, (-y - _cy) / ry])) % 360

        if (sweepFlag === 0 && _dTheta > 0) _dTheta -= 360
        if (sweepFlag === 1 && _dTheta < 0) _dTheta += 360

        return {
            centerX: cx,
            centerY: cy,
            theta,
            dTheta: this.rad(_dTheta),
            rx: rx,
            ry: ry
        }
    }

    deg = (rad: number): number => {
        const { PI } = Math
        return rad * 180 / PI
    }

    rad = (deg: number): number => {
        const { PI } = Math
        return deg * PI / 180
    }

    getCursorByPathType(t: PathType): string {
        if (t === DEFAULT_PATH_TYPE) {
            return 'copy'
        }

        const r = 10
        const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${2 * r}px' height='${2 * r}px'>` +
            `<circle cx='${r}' cy='${r}' r='${r - 4}' stroke='${Theme.steelblue}' stroke-width='1' fill='transparent' />` +
            `<text x='${r}' y='${r + 1}' fill='${Theme.blue}' dominant-baseline='middle' text-anchor='middle' font-size='${r - 1}px'>${t}</text>` +
            `</svg>`

        return `url("data:image/svg+xml;base64,${btoa(svg)}") ${r} ${r}, copy`
        //return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${r} ${r}, copy`
    }

    setRealSize = (newSize: Size) => {
        //TODO?
    }

    render() {
        const { data, size, isDraggable, activeShapeId } = this.props
        const { isActive, activeKey, dx, dy, dcx, dcy, dcx2, dcy2, t } = this.state

        const r = getHandleSize()
        const rectSize = 2 * r

        const lineStyle = {
            strokeWidth: '1px',
            stroke: Theme.steelblue,
        }

        const handleStyle: CSSProperties = {
            cursor: `url('${URL.publicPath}/img/cursors/hand-pointer.cur') -8 -8, pointer`,
            pointerEvents: 'auto'
        }

        const dashArray = '5 1'
        const pointsData = this.getPointsData()
        const letterSpacing = data.letter_spacing ? data.letter_spacing : 0
        const isShadow = data.shadow_color && data.shadow_sigma
        const isGradient = data.gradient_id && data.gradient

        return (
            <g className="draggable" width={size.width} height={size.height}>
                {isActive &&
                    <rect
                        ref={this.rectangleRef}
                        transform={`rotate(${-(data.angle || 0)}), translate(${-data.x},${-data.y})`}
                        width={size.width}
                        height={size.height}
                        fill={'transparent'}
                        style={{
                            cursor: isActive ? this.getCursorByPathType(t) : 'pointer',
                            pointerEvents: isActive && isDraggable ? 'fill' : 'none'
                        }}
                        onContextMenu={(e) => this.setState({isActive: false})}
                    />
                }

                {isGradient &&
                    <GradientDefs gradient={data.gradient} idx={data.gradient_id} />
                }

                {data.points &&
                    <path
                        key={`l${activeKey}`}
                        ref={this.pathRef}
                        d={pointsData}
                        strokeWidth={data.stroke_width || (isActive && [CURVED_TEXT_TYPE].includes(data.type) ? 1 : 0)}
                        stroke={(data.type === TEXT_INSIDE_SHAPE_TYPE && !isActive) ? 'transparent' : (data.stroke_color ?? ([CURVED_TEXT_TYPE, TEXT_INSIDE_SHAPE_TYPE].includes(data.type) ? Theme.blue : 'transparent'))}
                        strokeLinejoin={'round'}
                        fill={isGradient ? GradientFillUrl(data.gradient_id) : (data.color || (data.type === CURVED_TEXT_TYPE ? Theme.steelblue : 'transparent'))}
                        fillOpacity={isGradient && data.gradient.opacity ? data.gradient.opacity / 100 : 1}
                        style={{
                            pointerEvents: data.type === TEXT_INSIDE_SHAPE_TYPE ? 'fill' : 'stroke'
                        }}
                        id={`curve${data.index}`}
                    />
                }

                {isShadow &&
                    <filter id={`shadow-${data.index}`} width="200%" height="200%" y="-50%" x="-50%">
                        <feDropShadow
                            dx={data.shadow_xoffset}
                            dy={data.shadow_yoffset}
                            stdDeviation={data.shadow_sigma}
                            floodColor={data.shadow_color}
                            floodOpacity={1 - data.shadow_opacity / 100}
                        />
                    </filter>
                }

                <g filter={isShadow ? `url(#shadow-${data.index})` : undefined}>

                {data.type === CURVED_TEXT_TYPE && data.points && data.text_content && data.text_stroke_underlayer && data.text_stroke_color && data.text_stroke_width &&
                    <React.Fragment>
                        <StrokeUnderlayerFilter id={this.underlayerFilterId} strokeWidth={data.text_stroke_width} />
                        <text fill={data.text_stroke_color} filter={strokeUnderlayerFilterUrl(this.underlayerFilterId, data.text_stroke_width)} style={{
                            fontFamily: `"${data.text_fontfamily}","Unicode-BMP-Fallback-SIL"`,
                            fontSize: `${data.text_fontsize}px`,
                            stroke: `${data.text_stroke_color}`,
                            strokeWidth: `${data.text_stroke_width}`,
                            letterSpacing: letterSpacing ? `${letterSpacing}px` : undefined,
                            pointerEvents: 'none',
                        }}>
                            <textPath alignmentBaseline={'baseline'} xlinkHref={`#curve${data.index}`}>{data.text_content}</textPath>
                        </text>
                    </React.Fragment>
                }

                {data.type === CURVED_TEXT_TYPE && data.points && data.text_content &&
                    <text fill={data.text_color ?? '#000000'} style={{
                        fontFamily: `"${data.text_fontfamily}","Unicode-BMP-Fallback-SIL"`,
                        fontSize: `${data.text_fontsize}px`,
                        stroke: data.text_stroke_color && data.text_stroke_width && !data.text_stroke_underlayer ? `${data.text_stroke_color}` : undefined,
                        strokeWidth: data.text_stroke_color && data.text_stroke_width && !data.text_stroke_underlayer ? `${data.text_stroke_width}` : undefined,
                        letterSpacing: letterSpacing ? `${letterSpacing}px` : undefined,
                    }}>
                        <textPath alignmentBaseline={'baseline'} xlinkHref={`#curve${data.index}`}>{data.text_content}</textPath>
                    </text>
                }

                {data.type === TEXT_INSIDE_SHAPE_TYPE && data.points && data.lines && data.lines.length > 0 &&
                    <RenderText
                        shape={data}
                        fontSize={data.text_fontsize}
                        width={data.width}
                        h={data.height}
                        setRealSize={this.setRealSize}
                        offsetY={0}
                        activeShapeId={activeShapeId}
                        editMode={this.props.editMode}
                    />
                }

                {data.points && isActive &&
                    data.points.map((point, key) => {
                        const xpos = point.x + (key === activeKey ? dx : 0)
                        const ypos = point.y + (key === activeKey ? dy : 0)

                        const cx = point.cx + (key === activeKey ? dcx : 0)
                        const cy = point.cy + (key === activeKey ? dcy : 0)

                        const cx2 = (typeof point.cx2 !== 'undefined' && point.cx2 !== null) ? point.cx2 + (key === activeKey ? dcx2 : 0) : 2 * xpos - cx
                        const cy2 = (typeof point.cy2 !== 'undefined' && point.cy2 !== null) ? point.cy2 + (key === activeKey ? dcy2 : 0) : 2 * ypos - cy

                        const t = point.t ?? DEFAULT_PATH_TYPE

                        const prevPoint = key > 0 ? data.points[key - 1] : null

                        let rx = abs(cx)
                        let ry = abs(cy)

                        let param = prevPoint ? this.getCenterParameters(
                            prevPoint.x, prevPoint.y,
                            xpos, ypos,
                            prevPoint.isLargeArc ? 1 : 0,
                            prevPoint.isSweep ? 1 : 0,
                            rx, ry,
                            prevPoint.xRotDegrees ? prevPoint.xRotDegrees : 0
                        ) : null

                        rx = param?.rx ? param.rx : rx
                        ry = param?.ry ? param.ry : ry

                        return <g key={key}>
                            {cx && cy &&
                                <React.Fragment>
                                    {key === activeKey &&
                                        <g>
                                            {['C', 'S'].includes(t) &&
                                                <line x1={cx} y1={cy} x2={xpos} y2={ypos} style={lineStyle} strokeDasharray={dashArray} />
                                            }

                                            {t === 'A' && param &&
                                                <React.Fragment key={`c1bezier${activeKey}${key}`}>
                                                    <g transform={point.xRotDegrees ? `rotate(${point.xRotDegrees} ${param.centerX} ${param.centerY})` : undefined}>
                                                        <line x1={param.centerX} y1={param.centerY} x2={param.centerX + rx} y2={param.centerY} style={lineStyle} strokeDasharray={dashArray} />
                                                        <line x1={param.centerX} y1={param.centerY} x2={param.centerX} y2={param.centerY - ry} style={lineStyle} strokeDasharray={dashArray} />

                                                        <ellipse
                                                            cx={param.centerX}
                                                            cy={param.centerY}
                                                            rx={rx}
                                                            ry={ry}
                                                            fill={Theme.green + '1a'}
                                                            strokeWidth={2}
                                                            strokeDasharray={dashArray}
                                                            stroke={Theme.blue}
                                                        />

                                                        <rect
                                                            x={param.centerX + rx - .5 * rectSize}
                                                            y={param.centerY - .5 * rectSize}
                                                            width={rectSize}
                                                            height={rectSize}
                                                            fill={'transparent'}
                                                            strokeWidth={rectSize / 4}
                                                            stroke={Theme.blue}
                                                            style={handleStyle}
                                                            className={'handles'}
                                                            ref={this.getHandleRef('c', key)}
                                                        />

                                                        <rect
                                                            x={param.centerX - .5 * rectSize}
                                                            y={param.centerY - ry - .5 * rectSize}
                                                            width={rectSize}
                                                            height={rectSize}
                                                            fill={'transparent'}
                                                            strokeWidth={rectSize / 4}
                                                            stroke={Theme.blue}
                                                            style={handleStyle}
                                                            className={'handles'}
                                                            ref={this.getHandleRef('c2', key)}
                                                        />
                                                    </g>
                                                </React.Fragment>
                                            }

                                            {['C', 'S'].includes(t) &&
                                                <React.Fragment key={`c1bezier${activeKey}${key}`}>
                                                    <rect
                                                        x={cx - rectSize / 2}
                                                        y={cy - rectSize / 2}
                                                        width={rectSize}
                                                        height={rectSize}
                                                        fill={'#58839d8f'}
                                                        strokeWidth={rectSize / 4}
                                                        stroke={isActive ? Theme.blue : 'transparent'}
                                                        style={handleStyle}
                                                        className={'handles'}
                                                        ref={this.getHandleRef('c', key)}
                                                    />
                                                    <text transform={`translate(${cx},${cy})`} dominantBaseline={"central"} textAnchor={"middle"} fontSize={rectSize}>{1}</text>

                                                    <line x1={cx2} y1={cy2} x2={xpos} y2={ypos} style={lineStyle} strokeDasharray={dashArray} />
                                                    <rect
                                                        x={cx2 - rectSize / 2}
                                                        y={cy2 - rectSize / 2}
                                                        width={rectSize}
                                                        height={rectSize}
                                                        fill={'#58839d8f'}
                                                        strokeWidth={rectSize / 4}
                                                        stroke={isActive ? Theme.blue : 'transparent'}
                                                        style={handleStyle}
                                                        className={'handles'}
                                                        ref={this.getHandleRef('c2', key)}
                                                    />
                                                    <text transform={`translate(${cx2},${cy2})`} dominantBaseline={"central"} textAnchor={"middle"} fontSize={rectSize}>{2}</text>
                                                </React.Fragment>
                                            }
                                        </g>
                                    }
                                </React.Fragment>
                            }

                            <circle
                                key={`pbezier_${activeKey}${key}`}
                                id={key === 0 ? `pbezier_${data.index}_${key}` : undefined}
                                cx={xpos}
                                cy={ypos}
                                r={r}
                                fill={isActive ? (activeKey === key ? Theme.red : Theme.blue) : 'transparent'}
                                strokeWidth={2}
                                stroke={isActive ? Theme.grey : 'transparent'}
                                style={{ cursor: 'move' }}
                                className={'handles'}
                                ref={this.getHandleRef('m', key)}
                            ><title>{AllPathTypesAndMeanings[t] || ''}</title>
                            </circle>
                            <text
                                transform={`translate(${xpos},${ypos})`}
                                dominantBaseline={"central"}
                                textAnchor={"middle"}
                                fontSize={rectSize}
                            >{t !== DEFAULT_PATH_TYPE && t}{key}
                            </text>
                        </g>
                    })
                    }
                                    </g>
            </g>
        )
    }
}

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        form: selectFormValues(state, getFormName(state.isAlt)),
        activeShapeId: state.activeShapeId,
        helper: state.helper,
    }
}

export default connect(mapStateToProps, {
    updateInputValue,
    setHelper,
})(PathSvg)